import React, { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Search, User, Bike, Building2 } from 'lucide-react';
import axios from 'axios';
import { API, getAuthHeader } from '../App';

const AutoFillInput = ({ 
  label, 
  value, 
  onChange, 
  onSelect, 
  type = 'rider', // 'rider', 'motorcycle', 'lessee'
  placeholder,
  required = false,
  className = '',
  searchBy = 'name', // 'name', 'emirates_id', 'plate'
  ...props 
}) => {
  const [searchQuery, setSearchQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 2) {
        performSearch(searchQuery);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, type, searchBy]);

  // Handle clicks outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        inputRef.current && 
        !inputRef.current.contains(event.target) &&
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const performSearch = async (query) => {
    if (!query || query.length < 2) return;
    
    setLoading(true);
    try {
      let endpoint = '';
      let searchParam = 'query';

      switch (type) {
        case 'rider':
          if (searchBy === 'emirates_id') {
            endpoint = `/lookup/rider-by-emirates-id`;
            searchParam = 'emirates_id';
          } else {
            endpoint = `/lookup/rider-by-name`;
          }
          break;
        case 'motorcycle':
          endpoint = `/lookup/motorcycle-by-plate`;
          searchParam = 'plate';
          break;
        case 'lessee':
          endpoint = `/lookup/lessee-by-name`;
          break;
        default:
          return;
      }

      const response = await axios.get(`${API}${endpoint}?${searchParam}=${encodeURIComponent(query)}`, {
        headers: getAuthHeader()
      });

      // Handle single result (emirates_id lookup returns single object or null)
      const results = Array.isArray(response.data) ? response.data : (response.data ? [response.data] : []);
      setSuggestions(results);
      setShowSuggestions(results.length > 0);
    } catch (error) {
      console.error('Search failed:', error);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);
    setSelectedItem(null);
    
    // Call parent onChange for controlled input
    if (onChange) {
      onChange(e);
    }
  };

  const handleSelectItem = (item) => {
    setSelectedItem(item);
    setShowSuggestions(false);
    
    // Update input value based on type and searchBy
    let displayValue = '';
    switch (type) {
      case 'rider':
        displayValue = searchBy === 'emirates_id' ? item.emirates_id : item.name;
        break;
      case 'motorcycle':
        displayValue = item.plate_number;
        break;
      case 'lessee':
        displayValue = item.company_name;
        break;
    }
    
    setSearchQuery(displayValue);
    
    // Call parent onSelect with full item data
    if (onSelect) {
      onSelect(item);
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'rider': return <User className="w-4 h-4" />;
      case 'motorcycle': return <Bike className="w-4 h-4" />;
      case 'lessee': return <Building2 className="w-4 h-4" />;
      default: return <Search className="w-4 h-4" />;
    }
  };

  const renderSuggestionItem = (item) => {
    switch (type) {
      case 'rider':
        return (
          <div className="p-3 hover:bg-slate-50 cursor-pointer border-b last:border-b-0">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="font-medium text-slate-900">{item.name}</div>
                <div className="text-sm text-slate-600">Emirates ID: {item.emirates_id}</div>
                <div className="text-sm text-slate-500">Mobile: {item.mobile_uae}</div>
                {item.assigned_bike_plate && (
                  <Badge variant="outline" className="mt-1 text-xs">
                    Bike: {item.assigned_bike_plate} ({item.assigned_bike_model})
                  </Badge>
                )}
              </div>
              <User className="w-5 h-5 text-slate-400" />
            </div>
          </div>
        );
      
      case 'motorcycle':
        return (
          <div className="p-3 hover:bg-slate-50 cursor-pointer border-b last:border-b-0">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="font-medium text-slate-900">{item.plate_number}</div>
                <div className="text-sm text-slate-600">{item.model_name} - {item.color}</div>
                <div className="text-sm text-slate-500">Chassis: {item.chassis_number}</div>
                {item.rider_name && (
                  <Badge variant="outline" className="mt-1 text-xs">
                    Rider: {item.rider_name}
                  </Badge>
                )}
                {item.lessee_name && (
                  <Badge variant="secondary" className="mt-1 ml-1 text-xs">
                    Lessee: {item.lessee_name}
                  </Badge>
                )}
              </div>
              <Bike className="w-5 h-5 text-slate-400" />
            </div>
          </div>
        );
      
      case 'lessee':
        return (
          <div className="p-3 hover:bg-slate-50 cursor-pointer border-b last:border-b-0">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="font-medium text-slate-900">{item.company_name}</div>
                <div className="text-sm text-slate-600">{item.owner_name}</div>
                <div className="text-sm text-slate-500">
                  {item.owner_mobile || item.contact_mobile}
                </div>
              </div>
              <Building2 className="w-5 h-5 text-slate-400" />
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className={`relative ${className}`}>
      {label && <Label className="mb-2 block">{label}</Label>}
      
      <div className="relative">
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400">
          {getIcon()}
        </div>
        
        <Input
          ref={inputRef}
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={() => {
            if (suggestions.length > 0) {
              setShowSuggestions(true);
            }
          }}
          placeholder={placeholder || `Search ${type}...`}
          required={required}
          className="pl-10"
          {...props}
        />
        
        {loading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-600"></div>
          </div>
        )}
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <Card 
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-1 max-h-80 overflow-y-auto shadow-lg border bg-white"
        >
          {suggestions.map((item, index) => (
            <div
              key={item.id || index}
              onClick={() => handleSelectItem(item)}
            >
              {renderSuggestionItem(item)}
            </div>
          ))}
        </Card>
      )}

      {/* Selected Item Display */}
      {selectedItem && (
        <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
          <div className="text-sm text-green-800">
            ✓ Selected: {
              type === 'rider' ? selectedItem.name :
              type === 'motorcycle' ? selectedItem.plate_number :
              type === 'lessee' ? selectedItem.company_name : ''
            }
          </div>
        </div>
      )}
    </div>
  );
};

export default AutoFillInput;